<template>
  <div>
    <WdsAlert v-if="error" error icon class="tw-mb-4">
      {{ $t('Error loading compliance data') }}
      <template #description>{{ error.message }}</template>
    </WdsAlert>
    <div v-else-if="loading" class="tw-p-4">
      <WdsSpinner indeterminate :size="40" />
    </div>
    <template v-else-if="isApplicable">
      <WdsText secondary class="tw-mb-6">
        {{
          $t(
            'As a freelancer operating in the Netherlands, you need to provide compliance information for regulatory requirements.',
          )
        }}
      </WdsText>

      <div v-if="kvkCertificateCompliance">
        <WdsHeading as="h3" type="title-small" class="tw-mb-2">
          {{ $t('KvK Certificate') }}
        </WdsHeading>
        <NlKvkCertificateForm
          compliance-name="FREELANCER_NL_KVK_CERTIFICATE"
          :compliance-data="kvkCertificateCompliance.data"
          @saved="onComplianceSaved"
        />
      </div>
    </template>
    <WdsAlert v-else info icon>
      {{ $t('Netherlands compliance settings are not applicable to your account.') }}
    </WdsAlert>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { WdsAlert, WdsHeading, WdsSpinner, WdsText } from '@worksome/wds-components'
import NlKvkCertificateForm from '@/components/compliances/settings/NlKvkCertificateForm.vue'
import { ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT } from '@/components/compliances/queries/Compliances'
import { ParseJSONPropertyInObject } from '@/utils/ParseJSONPropertyInObject'

const complianceNames = ['FREELANCER_NL_KVK_CERTIFICATE']

const { result, loading, error, refetch } = useQuery(
  gql`
    query freelancerNlCompliancesQuery($complianceNames: [ComplianceName!]!) {
      me {
        activeAccount {
          __typename
          ...ActiveCompliancesOnAccount
        }
      }
    }
    ${ACTIVE_COMPLIANCES_ON_ACCOUNT_FRAGMENT}
  `,
  { complianceNames },
)

const onComplianceSaved = () => {
  refetch()
}

const activeAccount = computed(() => result?.value?.me?.activeAccount)

const compliances = computed(
  () =>
    activeAccount.value?.compliances
      ?.filter((compliance) => compliance.isApplicable)
      ?.map((compliance) => ParseJSONPropertyInObject('data', compliance)) || [],
)

const kvkCertificateCompliance = computed(() =>
  compliances.value.find((c) => c.name === 'FREELANCER_NL_KVK_CERTIFICATE'),
)

const isApplicable = computed(() => compliances.value.length > 0)
</script>
